import { faFilter, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormik } from 'formik';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { dataProductReportHead } from '~/app/constants';
import { useAppDispatch, useAppSelector } from '~/app/hooks/hooks';
import { iQueries, iReport } from '~/app/models/report';
import { filterEmptyProperties, reportSearchCriteria } from '~/app/utils';
import { FilterButtons, ResetFilterButton } from '~/components/common/Filter/filterInput';
import Pagination from '~/components/common/Pagination';
import { Table } from '~/components/common/Table';
import { NoRecord } from '~/components/pages/Err';
import { getProductReport, reportReset, resetStatus, setQueries } from '../redux/actions';
import filterSchema from '../schema';
import { Input } from '~/components/common/Input';
interface iInitialValues {
  from: string;
  to: string;
}
interface props {}

export const ProductReport: React.FC<props> = (props: props) => {
  const dispatch = useAppDispatch();
  const [isEnableFilterBoard, setIsEnableFilterBoard] = useState<boolean>(false);
  const { accessToken, currentStore } = useAppSelector((s) => s.authReducer);

  const { products, status: productStatus, productQueries } = useAppSelector((s) => s.reportReducer);
  const { items, total_count, total } = products;

  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = searchParams.get('pageSize') || 10;
  const currentPage = searchParams.get('currentPage') || 1;
  const from = searchParams.get('from') || '';
  const isFilter = searchParams.get('isFilter') || '';
  const to = searchParams.get('to') || '';

  const initialValues: iInitialValues = {
    from,
    to,
  };

  const { values, handleChange, resetForm, handleSubmit, setFieldValue, errors } = useFormik({
    initialValues,
    validationSchema: filterSchema,
    validateOnChange: false,
    onSubmit: (values) => {
      const filteredObject = filterEmptyProperties(values);
      setSearchParams({
        ...filteredObject,
        currentPage: currentPage.toString(),
        pageSize: pageSize.toString(),
        isFilter: 'true',
      });
      resetUrlData();
    },
  });

  const type = 'product';

  // Change Page Size
  const onChangePageSizeEvent = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const data = {
      from,
      to,
    };
    const filteredObject = filterEmptyProperties(data);
    setSearchParams({ ...filteredObject, pageSize: value, currentPage: '1', isFilter: (!!Object.keys(filteredObject).length).toString() });
    resetUrlData();
  };

  // Change current Page
  const onChangePage = (currentPage: number) => {
    const data = {
      from,
      to,
    };
    const filteredObject = filterEmptyProperties(data);
    setSearchParams({
      ...filteredObject,
      pageSize: pageSize.toString(),
      currentPage: currentPage.toString(),
      isFilter: 'true',
    });
    resetUrlData();
  };

  const resetUrlData = (): void => {
    const { search } = window.location;

    const payload: iQueries = {
      type: type,
      query: search,
    };

    dispatch(setQueries(payload));
  };

  const fetchData = () => {
    const data = {
      from,
      to,
    };
    const filteredObject = filterEmptyProperties(data);

    if (!Object.keys(filteredObject).length) {
      return;
    }

    const payload: iReport = {
      accessToken,
      currentStore,
      searchUrl: reportSearchCriteria({
        pageSize: +pageSize,
        currentPage: +currentPage,
        ...filteredObject,
      }),
    };
    dispatch(getProductReport(payload));
  };

  useEffect(() => {
    if (Object.keys(filterEmptyProperties(values)).length) {
      setSearchParams({
        ...values,
        isFilter: 'true',
        pageSize: pageSize.toString(),
        currentPage: currentPage.toString(),
      });
    }

    return () => {
      dispatch(resetStatus());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFilter) {
      resetUrlData();
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, currentPage, productQueries]);

  useEffect(() => {
    if (!Object.keys(filterEmptyProperties(values)).length) {
      dispatch(reportReset(type));
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const removeFilterSection = (key: string) => {
    if (['from', 'to'].includes(key)) {
      setFieldValue('from', '');
      setFieldValue('to', '');
      setSearchParams({ ...values, currentPage: currentPage.toString(), pageSize: pageSize.toString(), from: '', to: '', isFilter: 'true' });
    } else {
      setFieldValue(key, '');
      setSearchParams({ ...values, currentPage: currentPage.toString(), pageSize: pageSize.toString(), [key]: '', isFilter: 'true' });
    }
    resetUrlData();
  };

  const resetFilterSection = () => {
    !isEnableFilterBoard && setIsEnableFilterBoard(true);
    resetForm();
    setSearchParams({});
    setFieldValue('from', '');
    setFieldValue('to', '');
    dispatch(reportReset(type));
  };

  const renderResetFilter = () => {
    return (
      <div className="text-start w-100 my-4 mt-xxl-0 d-flex align-items-center justify-content-end justify-content-md-start">
        <div
          className="filters--button w-fit-content d-flex justify-content-center align-items-center btn btn-border-radius fw-medium border-rd-25 fs-14 text-uppercase mt-0"
          onClick={() => setIsEnableFilterBoard(!isEnableFilterBoard)}
        >
          <FontAwesomeIcon icon={faFilter} className="me-12" /> Filters
        </div>

        {productStatus !== 'pending' && <span className="ms-4 fw-normal border-0 fs-14">Total {total_count} Record(s) Found</span>}
      </div>
    );
  };

  const renderFilterSelected = () => {
    const data = {
      from,
      to,
    };
    const filteredObject: any = filterEmptyProperties(data);
    const filterData: any = Object.keys(filteredObject);
    if (!filterData.length) return null;

    return (
      <div className="col-12 mb-5">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span className="p-3 fs-6 fw-medium">Active Filters</span>
          <ResetFilterButton onClick={() => resetFilterSection()} />
        </div>
        <div className="border-top border-bottom p-3 align-items-center">
          {filterData.map((key: string, i: number) => {
            return (
              <div className="filter-active" key={i}>
                {key === 'from' && to && from && (
                  <>
                    <span className="fw-medium me-1">Filter Date :</span>
                    <span className="fw-light">
                      {moment(from).format('MM/DD/YYYY')} -{moment(to).format('MM/DD/YYYY')}
                    </span>
                    <FontAwesomeIcon
                      className="ms-2 btn btn-gray reset-filter-active fs-7 rounded-circle"
                      icon={faXmark}
                      onClick={() => removeFilterSection(key)}
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderFilterBoard = () => {
    return (
      <div className={`bg-white filter-board${isEnableFilterBoard ? '' : ' d-none'} p-4 wrapper mb-5`}>
        <div className="filter-section bg-white">
          <div className="row">
            <h5 className="col-12 mb-4 text-dark filter-title">Filters</h5>

            <div className="col-sm-12 col-lg-3 mb-3 col-xl-3">
              <label htmlFor="" className="col-lg-10 fw-medium text-dark pb-2  fs-14">
                Start Date<span className="fw-semibold text-danger">*</span>
              </label>
              <Input type="date" className="w-100" name="from" value={values.from} onChange={handleChange} />
              <span className="fs-7 text-danger">{errors?.from}</span>
            </div>
            <div className="col-sm-12 col-lg-3 mb-3 col-xl-3">
              <label htmlFor="" className="col-lg-10 fw-medium text-dark pb-2  fs-14">
                End Date<span className="fw-semibold text-danger">*</span>
              </label>
              <Input type="date" className="w-100" name="to" value={values.to} onChange={handleChange} />
              <span className="fs-7 text-danger">{errors?.to}</span>
            </div>
          </div>

          <FilterButtons onSubmit={() => handleSubmit()} status={productStatus} setIsEnableFilter={() => setIsEnableFilterBoard(false)} />
        </div>
      </div>
    );
  };

  const renderTable = () => {
    return (
      <Table className="order-table bg-white wrapper overflow-hidden" status={productStatus || 'rejected'} dataTableHead={dataProductReportHead}>
        <>
          {items?.length ? (
            items.map((item, i: number) => (
              <tr key={i}>
                {/* <td className=" td-item fs-14 px-2 py-3 text-end">{item.product_id}</td> */}
                <td className=" td-item fs-14 px-2 py-3 text-start">{item.order_items_name}</td>
                <td className=" td-item fs-14 px-2 py-3 text-start">{item.order_items_sku}</td>
                <td className=" td-item fs-14 px-2 py-3 text-end">{+item.ordered_qty}</td>
              </tr>
            ))
          ) : (
            <NoRecord tableHeaders={dataProductReportHead} />
          )}
          {items?.length ? (
            <tr>
              <td className="td-item fs-14 bg-total fw-medium px-2 py-3 text-start">TOTAL</td>
              <td className="td-item fs-14 bg-total fw-medium px-2 py-3 text-start"></td>
              <td className="td-item fs-14 bg-total fw-medium px-2 py-3 text-end">{total[0].qty_ordered}</td>
            </tr>
          ) : (
            ''
          )}
        </>
      </Table>
    );
  };

  return (
    <>
      {renderResetFilter()}
      {renderFilterBoard()}
      {isFilter && renderFilterSelected()}
      {renderTable()}
      <Pagination
        currentPage={+currentPage}
        pageSize={+pageSize}
        status={productStatus}
        totalCount={total_count}
        onChangePageEvent={onChangePage}
        onChangePageSizeEvent={onChangePageSizeEvent}
      />
    </>
  );
};
